import { parseLocaleNumber } from 'Helpers/currencyFormat';

// Zero-suppression helpers for the "Hide rows with no values" toggle.
//
// A row counts as empty when every *displayed* monetary figure is zero/nil.
// "Displayed" always includes the three time bases (Month / Reporting Period /
// Lifetime) and only the supplementary columns currently switched on, so the
// same row can suppress under one column preset and survive under another.
// Percentage columns are ratios, not values, so they never keep a row alive.

const toNumber = (value) => {
    if (value === null || value === undefined || value === '') {
        return 0;
    }

    const n = typeof value === 'number' ? value : parseLocaleNumber(value);

    return Number.isFinite(n) ? n : 0;
};

const allZero = (values) => values.every((v) => toNumber(v) === 0);

/**
 * Emptiness test for a summary/middle-tier row (department or cost-centre).
 *
 * @param {Object} rowData        a summary row (`{ department, data: {...} }`)
 * @param {Object} visibleColumns supplementary column → boolean map
 */
export function isSummaryRowEmpty(rowData, visibleColumns = {}) {
    const d = rowData && rowData.data;

    if (!d) {
        return true;
    }

    const figures = [
        d.month && d.month.total,
        d.month && d.month.total_actual,
        d.month && d.month.variance,
        d.period && d.period.total,
        d.period && d.period.total_actual,
        d.period && d.period.variance,
        d.total && d.total.total,
        d.total && d.total.total_actual,
        d.total && d.total.variance,
    ];

    if (visibleColumns.committed) figures.push(d.committed && d.committed.total);
    if (visibleColumns.uncommitted) figures.push(d.uncommitted && d.uncommitted.total);
    if (visibleColumns.risk) figures.push(d.risk && d.risk.total);
    if (visibleColumns.opportunity) figures.push(d.opp && d.opp.total);
    if (visibleColumns.forecast) figures.push(d.afc && d.afc.total);

    return allZero(figures);
}

/**
 * Emptiness test for a detailed-table row.
 *
 * @param {Object} data           a detailed row payload
 * @param {Object} visibleColumns supplementary column → boolean map
 * @param {Object} opts
 * @param {boolean} opts.monthlyPhasing whether per-month columns are shown
 * @param {Object}  opts.displayDates    per-month display flags
 * @param {Array}   opts.datesArray      the report's month keys
 */
export function isDetailedRowEmpty(data, visibleColumns = {}, { monthlyPhasing = false, displayDates = {}, datesArray = [] } = {}) {
    if (!data) {
        return true;
    }

    const figures = [
        data.month_budget_total,
        data.month_variance,
        data.period_budget_total,
        data.period_actual_total,
        data.period_variance,
        data.budget_total,
        data.actual_total,
        data.total_variance,
    ];

    if (monthlyPhasing) {
        datesArray.forEach((month) => {
            if (displayDates[month] && data.includes && data.includes[month]) {
                figures.push(data.includes[month].actual_total);
            }
        });
    }

    if (visibleColumns.committed) figures.push(data.committed_total);
    if (visibleColumns.uncommitted) figures.push(data.uncommitted_total);
    if (visibleColumns.risk) figures.push(data.risk);
    if (visibleColumns.opportunity) figures.push(data.opp);
    if (visibleColumns.forecast) figures.push(data.afc);

    return allZero(figures);
}
